/**
 * WachtwoordWijzigenPage — Geforceerde wachtwoord-wijziging na eerste login.
 *
 * Toegankelijk voor elke ingelogde user. Bij must_change_password=true (gezet
 * door admin bij partner-approve) wordt de gebruiker via ProtectedRoute hier
 * naartoe geforceerd — kan niet weg tot wachtwoord is gewijzigd.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, Eye, EyeOff, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function WachtwoordWijzigenPage() {
  const { user, getToken, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const forced = !!user?.must_change_password;

  const checks = {
    length: next.length >= 8,
    upper: /[A-Z]/.test(next),
    digit: /\d/.test(next),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=]/.test(next),
    differ: next && next !== current,
    match: next && next === confirm,
  };
  const score = Object.values(checks).filter(Boolean).length;
  const strong = checks.length && checks.upper && checks.digit && checks.differ && checks.match;

  const submit = async (e) => {
    e.preventDefault();
    if (!checks.length) { toast.error('Wachtwoord moet minimaal 8 tekens zijn'); return; }
    if (!checks.differ) { toast.error('Nieuw wachtwoord moet anders zijn dan het huidige'); return; }
    if (!checks.match) { toast.error('Wachtwoorden komen niet overeen'); return; }
    setSaving(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      // Backend issues a NEW JWT — replace it so we don't get logged out via
      // token-rotation guard on next request.
      if (res.data?.token) {
        localStorage.setItem('portal_token', res.data.token);
      }
      toast.success('Wachtwoord gewijzigd');
      await refreshUser();
      navigate('/platform', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wijzigen mislukt');
    }
    setSaving(false);
  };

  return (
    <div style={{ padding: '40px 24px', color: 'white', maxWidth: 560, margin: '0 auto' }} data-testid="change-password-page">
      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 14, padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ background: '#D4A017', width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShieldCheck size={22} color="#0f172a" />
          </div>
          <div>
            <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, margin: 0 }}>
              {forced ? 'Stel uw eigen wachtwoord in' : 'Wachtwoord wijzigen'}
            </h1>
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '2px 0 0' }}>
              {forced ? 'Eerst even uw tijdelijke wachtwoord vervangen.' : 'Kies een nieuw veilig wachtwoord.'}
            </p>
          </div>
        </div>

        {forced && (
          <div style={{ background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.3)', borderRadius: 10, padding: 12, marginBottom: 18, display: 'flex', gap: 10, fontSize: 13, color: '#fde68a' }}>
            <AlertCircle size={18} color="#D4A017" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>Eenmalige verplichte wijziging.</strong>
              <p style={{ margin: '4px 0 0', color: '#fde68a99', lineHeight: 1.5 }}>
                U logde in met een tijdelijk wachtwoord uit uw welkomstmail. Voor uw veiligheid moet u dit nu vervangen door een eigen wachtwoord voordat u verder kunt.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={submit}>
          <PasswordField label="Huidig wachtwoord" value={current} onChange={setCurrent} show={showCurrent} toggle={() => setShowCurrent(!showCurrent)} testId="current-password" autoFocus />
          <PasswordField label="Nieuw wachtwoord" value={next} onChange={setNext} show={showNew} toggle={() => setShowNew(!showNew)} testId="new-password" />
          <PasswordField label="Bevestig nieuw wachtwoord" value={confirm} onChange={setConfirm} show={showNew} toggle={() => setShowNew(!showNew)} testId="confirm-password" />

          {/* Strength meter */}
          {next && (
            <div style={{ marginTop: 6, marginBottom: 14 }}>
              <div style={{ height: 4, background: '#1e293b', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${(score / 6) * 100}%`,
                  height: '100%',
                  background: score >= 5 ? '#10b981' : score >= 3 ? '#D4A017' : '#ef4444',
                  transition: 'width 0.2s',
                }} />
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', fontSize: 11, color: '#94a3b8' }}>
                <Check ok={checks.length}>Minimaal 8 tekens</Check>
                <Check ok={checks.upper}>Bevat een hoofdletter (aanbevolen)</Check>
                <Check ok={checks.digit}>Bevat een cijfer (aanbevolen)</Check>
                <Check ok={checks.special}>Bevat een speciaal teken (aanbevolen)</Check>
                <Check ok={checks.differ}>Anders dan huidig wachtwoord</Check>
                <Check ok={checks.match}>Komt overeen met bevestiging</Check>
              </ul>
            </div>
          )}

          <button
            type="submit"
            disabled={saving || !strong}
            data-testid="change-password-submit"
            style={{
              width: '100%',
              marginTop: 18,
              background: strong ? '#D4A017' : '#475569',
              color: strong ? '#0f172a' : '#94a3b8',
              fontWeight: 800,
              padding: '14px 24px',
              borderRadius: 10,
              border: 'none',
              cursor: saving || !strong ? 'not-allowed' : 'pointer',
              fontSize: 15,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Wachtwoord wijzigen
          </button>
        </form>

        {forced && (
          <button
            type="button"
            onClick={logout}
            data-testid="change-password-logout"
            style={{ width: '100%', marginTop: 10, background: 'transparent', color: '#94a3b8', padding: '8px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
          >
            Liever later? Uitloggen
          </button>
        )}
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, toggle, testId, autoFocus }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, display: 'block' }}>{label}</span>
      <div style={{ position: 'relative' }}>
        <Lock size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          autoFocus={autoFocus}
          style={{
            width: '100%',
            background: '#0f172a',
            color: 'white',
            border: '1px solid #1e293b',
            padding: '11px 38px 11px 38px',
            borderRadius: 8,
            fontSize: 14,
          }}
        />
        <button type="button" onClick={toggle} tabIndex={-1} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer' }}>
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </label>
  );
}

function Check({ ok, children }) {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 6, color: ok ? '#10b981' : '#64748b', padding: '2px 0' }}>
      <span style={{ width: 12, textAlign: 'center', fontWeight: 700 }}>{ok ? '✓' : '·'}</span>
      <span>{children}</span>
    </li>
  );
}
